import { inject, Injectable, signal } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { ApiCallingV2Service, API_CONFIG, DropdownData, SetupUser } from '@zayan-erp/shared';
import {
  DsmApsStatus,
  DsmApsStatusLabels,
  DsmApsStatusOption,
  DsmBuyerContracts,
  DsmBuyerSelections,
  DsmDealHeader,
  DsmDealLocationWarnings,
  DsmDealsLiteResponse,
  DsmProject,
  DsmProjectLookup,
  DsmSalesOfficeOption,
  DsmSalesOfficeProjectCount,
  DsmSpecLot,
  DsmSpecLotModelOption
} from '../models/dsm-aps.models';
import { DsmRoomReplacementReview } from '../models/dsm-room-replacement-review.models';

@Injectable({ providedIn: 'root' })
export class DsmApsService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  #baseUrl = this.#apiConfig.baseUrlV2;

  readonly refreshTrigger = signal(0);

  triggerSpecLotsRefresh(): void {
    this.refreshTrigger.update(v => v + 1);
  }

  getDealsLite(
    statusIds?: string[],
    projectId?: string,
    lotId?: string,
    dateFilters?: {
      fromContractDate?: string;
      toContractDate?: string;
      fromTargetClosingDate?: string;
      toTargetClosingDate?: string;
    },
    hasPendingLocationRules?: boolean
  ): Observable<DsmDealsLiteResponse> {
    const params: string[] = [];
    if (statusIds?.length) {
      for (const id of statusIds) params.push(`Status=${id}`);
    }
    if (projectId) params.push(`ProjectId=${projectId}`);
    if (lotId) params.push(`LotId=${lotId}`);
    if (dateFilters?.fromContractDate) params.push(`FromContractDate=${dateFilters.fromContractDate}`);
    if (dateFilters?.toContractDate) params.push(`ToContractDate=${dateFilters.toContractDate}`);
    if (dateFilters?.fromTargetClosingDate) params.push(`FromTargetClosingDate=${dateFilters.fromTargetClosingDate}`);
    if (dateFilters?.toTargetClosingDate) params.push(`ToTargetClosingDate=${dateFilters.toTargetClosingDate}`);
    if (hasPendingLocationRules != null) params.push(`HasPendingLocationRules=${hasPendingLocationRules}`);
    params.push(`PageSize=1000`);
    const query = params.length ? `?${params.join('&')}` : '';
    return this.#http.getData<DsmDealsLiteResponse>(`${this.#baseUrl}/deals/lite${query}`, {
      showLoader: false,
      showMessage: false
    });
  }

  getLocationWarnings(dealId: string): Observable<DsmDealLocationWarnings | null> {
    return this.#http
      .getData<DsmDealLocationWarnings[]>(`${this.#baseUrl}/deals/location-warnings?dealIds=${dealId}`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(map(warnings => warnings?.[0] ?? null));
  }

  getDealStatuses(): Observable<DsmApsStatusOption[]> {
    const statuses = Object.values(DsmApsStatus).map(id => ({
      id,
      firstLabel: DsmApsStatusLabels[id as DsmApsStatus]
    }));
    return of(statuses);
  }

  getSalesOffices(): Observable<DsmSalesOfficeOption[]> {
    return this.#http
      .getData<any[]>(`${this.#baseUrl}/lookup/sales-offices`, { showLoader: false, showMessage: false })
      .pipe(
        map(data =>
          (data ?? []).map((item: any) => ({
            id: item.id,
            firstLabel: item.name,
            currencyCode: item.currencyCode
          }))
        )
      );
  }
  getSalesOfficesWithProjectCount(): Observable<DsmSalesOfficeOption[]> {
    return this.#http
      .getData<any[]>(`${this.#baseUrl}/SalesOffice/with-project-count`, { showLoader: false, showMessage: false })
      .pipe(
        map(data =>
          (data ?? []).map((item: DsmSalesOfficeProjectCount) => ({
            id: item.id,
            firstLabel: item.name,
            secondLabel: item.projectCount
          }))
        )
      );
  }

  getDealHeader(dealId: string, showLoader = true): Observable<DsmDealHeader> {
    return this.#http.getData<DsmDealHeader>(`${this.#baseUrl}/deals/${dealId}`, {
      showLoader,
      showMessage: false
    });
  }

  getBuyerSelections(dealId: string, showLoader = true): Observable<DsmBuyerSelections> {
    return this.#http.getData<DsmBuyerSelections>(`${this.#baseUrl}/deals/${dealId}/buyer-selections`, {
      showLoader,
      showMessage: false
    });
  }

  getBuyerContracts(dealId: string, showLoader = true): Observable<DsmBuyerContracts> {
    return this.#http.getData<DsmBuyerContracts>(`${this.#baseUrl}/deals/${dealId}/buyer-contracts`, {
      showLoader,
      showMessage: false
    });
  }

  getRoomReplacementReview(dealId: string, showLoader = true): Observable<DsmRoomReplacementReview> {
    return this.#http.getData<DsmRoomReplacementReview>(`${this.#baseUrl}/deals/${dealId}/room-replacement-review`, {
      showLoader,
      showMessage: false
    });
  }

  getSalesOfficeProjects(salesOfficeId: string, assignedToMeOnly = false): Observable<DropdownData[]> {
    return this.#http
      .getData<DsmProject[]>(
        `${this.#baseUrl}/SalesOffice/${salesOfficeId}/projects?assignedToMeOnly=${assignedToMeOnly}`,
        {
          showLoader: false,
          showMessage: false
        }
      )
      .pipe(
        map(
          data =>
            (data ?? []).map((p: DsmProject) => ({
              id: p.projectId ?? p.id ?? '',
              firstLabel: p.projectCode ?? p.projectName ?? '',
              secondLabel: p.projectName ?? ''
            })) as DropdownData[]
        )
      );
  }

  getSpecLotModelElevations(projectId: string): Observable<DsmSpecLotModelOption[]> {
    return this.#http
      .getData<DsmSpecLotModelOption[] | null>(
        `${this.#baseUrl}/Lot/spec-lots/model-elevations?projectId=${projectId}`,
        {
          showLoader: false,
          showMessage: false
        }
      )
      .pipe(map(models => models ?? []));
  }

  getModelsByLot(lotId: string): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.#baseUrl}/Lot/${lotId}/models`, { showLoader: false, showMessage: false })
      .pipe(
        map(
          res =>
            (res ?? []).map((item: any) => ({
              id: item.modelId ?? item.id,
              firstLabel: item.modelName ?? item.name,
              secondLabel: item.projectName ?? '',
              data: item.id
            })) as DropdownData[]
        )
      );
  }

  updateSpecLot(
    lotId: string,
    payload: { lotId: string; modelId: string | null; elevationId: string | null; lotTypeId?: string | null },
    lotNo?: string
  ): Observable<unknown> {
    return this.#http.putData(`${this.#baseUrl}/Lot/spec-lots/${lotId}`, payload, {
      showLoader: true,
      showMessage: !lotNo,
      message: 'Spec lot updated successfully',
      showError: true,
      errorMessage: lotNo ? `Failed to update lot ${lotNo}` : undefined
    });
  }

  toggleReleaseToConstruction(lotId: string, lotNo?: string): Observable<unknown> {
    return this.#http.putData(`${this.#baseUrl}/Lot/${lotId}/release-to-construction/toggle`, null, {
      showLoader: true,
      showMessage: !lotNo,
      message: 'Release to construction updated successfully',
      showError: true,
      errorMessage: lotNo ? `Failed to update lot ${lotNo}` : undefined
    });
  }

  checkModelElevationEligibility(params: {
    lotId: string;
    modelId: string;
    elevationId: string;
  }): Observable<{ isEligible: boolean; violatedRules: string[] }> {
    const query = `?lotId=${params.lotId}&modelId=${params.modelId}&elevationId=${params.elevationId}`;
    return this.#http.getData<{ isEligible: boolean; violatedRules: string[] }>(
      `${this.#baseUrl}/Lot/model-elevation-eligibility${query}`,
      { showLoader: true, showMessage: false }
    );
  }

  getSpecLotDetails(lotId: string): Observable<DsmSpecLot> {
    return this.#http.getData<DsmSpecLot>(`${this.#baseUrl}/Lot/spec-lots/${lotId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  getSpecLots(projectId: string): Observable<DsmSpecLot[]> {
    return this.#http.getData<DsmSpecLot[]>(`${this.#baseUrl}/Lot/spec-lots?projectId=${projectId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  getDsmUsers(): Observable<SetupUser[]> {
    return this.#http.getData(`${this.#apiConfig.baseUrl}dsm/users`, { showLoader: true, showMessage: false });
  }

  getDsmProjects(showLoader = true): Observable<DsmProjectLookup[]> {
    return this.#http.getData<DsmProjectLookup[]>(`${this.#baseUrl}/lookup/projects`, {
      showLoader,
      showMessage: false
    });
  }

  updateDsmUser(obj: any, id: string): Observable<unknown> {
    return this.#http.postData(
      `${this.#baseUrl}/dsm/users/${id}`,
      { showLoader: false, showMessage: true, message: 'DSM user updated successfully' },
      obj
    );
  }
}
